/**
 * JOB HUNTER › OVERVIEW — /job-hunter
 *
 * The agent at a glance for the active candidate: setup checklist until it's
 * live, then KPIs, the funnel from discovered jobs to interviews, the daily
 * trend, the best open matches, sponsorship mix, countries, AI budget and the
 * latest agent runs.
 */
import { ReactNode, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Link, useNavigate } from 'react-router-dom';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import toast from 'react-hot-toast';
import {
  Activity, ArrowRight, Award, BellRing, Bot, Building2, Check, Coins, Inbox, LoaderCircle, MessageCircle, Send, Sparkles, Target, TrendingUp, TriangleAlert, UserRound, Zap,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { JH, JobHunterShell, RunAgentButton, useAgentRun, useRunFinished } from '@/components/job-hunter/JobHunterShell';
import { MatchCard } from '@/components/job-hunter/MatchCard';
import { AgentCard } from '@/components/job-hunter/AgentCard';
import { ApplicationDrawer } from '@/components/job-hunter/ApplicationDrawer';
import { Meter, Notice, SignalBadge } from '@/components/job-hunter/ui';
import { useJobHunter } from '@/lib/job-hunter/store';
import { SIGNAL_META, STATUS_META, countryName, flag, fmtDateTime, num, timeAgo } from '@/lib/job-hunter/format';
import { jobHunterService, type JhOverview, type JhSignal } from '@/services/job-hunter.service';

function Kpi({ label, value, hint, icon, tone }: { label: string; value: string | number; hint?: string; icon: React.ReactNode; tone: string }) {
  return (
    <div className="jh-kpi">
      <div className="jh-kpi-label">{label}<span className={`grid h-7 w-7 place-items-center rounded-lg ${tone}`}>{icon}</span></div>
      <div className="jh-kpi-value">{value}</div>
      {hint && <div className="jh-kpi-hint">{hint}</div>}
    </div>
  );
}

function Setup({ o, cvs, onSeed, seeding }: { o: JhOverview; cvs: number; onSeed: () => void; seeding: boolean }) {
  const hasSources = Object.values(o.sources).some((n) => (n ?? 0) > 0);
  const hasJobs = o.jobs.total > 0;
  const alerts = o.profile.notifyEnabled && !!o.profile.notifyPhone;
  const uid = o.profile.profileUid;
  const steps: Array<{ done: boolean; title: string; body: string; to?: string; label: string; custom?: ReactNode }> = [
    { done: cvs > 0, title: 'Add a CV', body: 'Agents score jobs against it; the AI reviews its health and fills the profile.', to: JH.candidate(uid, 'cvs'), label: cvs ? 'CV library' : 'Add a CV' },
    { done: o.agents.length > 0, title: 'Create an agent', body: 'One per kind of job, with its own CV, roles and countries.', to: JH.AGENTS, label: o.agents.length ? 'Manage' : 'Create' },
    { done: hasSources, title: 'Load sources', body: 'Boards and verified employer careers feeds.', to: hasSources ? JH.SOURCES : undefined, label: 'Manage', custom: hasSources ? undefined : <Button size="sm" loading={seeding} onClick={onSeed}>Load starter set</Button> },
    { done: hasJobs, title: 'First run', body: 'Poll, screen and score the jobs.', label: '', custom: hasSources && !hasJobs ? <RunAgentButton size="sm" /> : undefined },
    { done: alerts, title: 'WhatsApp alerts', body: 'Strong matches arrive with Apply / Skip.', to: JH.candidate(uid, 'alerts'), label: alerts ? 'Edit' : 'Set up' },
  ];
  if (steps.every((s) => s.done)) return null;
  const doneCount = steps.filter((s) => s.done).length;
  const next = steps.findIndex((s) => !s.done);
  return (
    <section className="jh-card mb-5 overflow-hidden @container">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="jh-eyebrow">Getting started</div>
          <div className="text-[15px] font-semibold tracking-tight">Put the agent to work <span className="font-normal text-text-muted">— visa-sponsored jobs, scored against the CV, nothing sent without your OK.</span></div>
        </div>
        <div className="flex w-full items-center gap-3 @3xl:w-60">
          <div className="jh-meter flex-1"><span style={{ width: `${(doneCount / steps.length) * 100}%`, background: 'var(--color-primary)' }} /></div>
          <span className="text-[12px] font-semibold tabular-nums text-text-secondary">{doneCount} of {steps.length}</span>
        </div>
      </div>
      <ol className="grid gap-px border-t border-border bg-border @2xl:grid-cols-2 @5xl:grid-cols-5">
        {steps.map((s, i) => {
          const isNext = i === next;
          return (
            <li key={s.title} className={clsx('flex gap-3 bg-surface px-4 py-3.5', isNext && 'bg-primary-soft/40')}>
              <span className={clsx('grid h-7 w-7 shrink-0 place-items-center rounded-full text-[12px] font-bold',
                s.done ? 'bg-success-soft text-success' : isNext ? 'bg-primary text-on-primary' : 'bg-surface-alt text-text-muted')}>
                {s.done ? <Check size={14} strokeWidth={3} /> : i + 1}
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <div className={clsx('text-[13px] font-semibold', s.done && 'text-text-secondary')}>{s.title}</div>
                <p className="mt-0.5 text-[12px] leading-snug text-text-muted">{s.body}</p>
                <div className="mt-2">
                  {s.custom ?? (s.to && (s.done
                    ? <Link to={s.to} className="inline-flex items-center gap-1 text-[12px] font-semibold text-primary hover:underline">{s.label}<ArrowRight size={12} /></Link>
                    : <Link to={s.to}><Button size="sm" variant={isNext ? 'primary' : 'secondary'}>{s.label}</Button></Link>))}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export default function JhOverviewPage() {
  const { profileUid, profile } = useJobHunter();
  const { running, start } = useAgentRun();
  const [runningUid, setRunningUid] = useState<string | null>(null);
  useEffect(() => { if (!running) setRunningUid(null); }, [running]);
  const nav = useNavigate();
  const [o, setO] = useState<JhOverview | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState<{ uid: string; action: string } | null>(null);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    if (!profileUid) return;
    try { setO(await jobHunterService.overview(profileUid)); } catch { /* toasted */ }
  }, [profileUid]);
  useEffect(() => { setO(null); void load(); }, [load]);
  useRunFinished(load);

  const seed = async () => {
    setSeeding(true);
    try { const r = await jobHunterService.seedSources(); toast.success(`${r.created} sources added — the agent starts polling them now`); await load(); }
    catch { /* toasted */ } finally { setSeeding(false); }
  };
  const decide = async (uid: string, action: 'apply' | 'save' | 'skip') => {
    setBusy({ uid, action });
    try { await jobHunterService.decide(uid, action); toast.success(action === 'apply' ? 'Approved — application prepared' : action === 'save' ? 'Saved' : 'Skipped'); if (action === 'apply') setOpen(uid); await load(); }
    catch { /* toasted */ } finally { setBusy(null); }
  };

  const signals = o ? (['explicit', 'likely', 'none', 'excluded'] as JhSignal[]).map((s) => ({ s, n: o.signals[s] ?? 0 })) : [];
  const signalTotal = signals.reduce((t, x) => t + x.n, 0);
  const maxCountry = Math.max(1, ...(o?.countries.map((c) => c.n) ?? [1]));
  const funnel = o ? [
    ['Discovered', o.funnel.discovered], ['Screened', o.funnel.screened], ['Passed filter', o.funnel.passedFilter],
    ['Proposed', o.funnel.proposed], ['Applied', o.funnel.applied], ['Interviews', o.funnel.interviews],
  ] as Array<[string, number]> : [];
  const funnelMax = Math.max(1, ...funnel.map(([, n]) => n));
  const lastRun = o?.runs[0];

  return (
    <JobHunterShell section="Overview" description="Your job-hunting agent at a glance">
      {!o ? <div className="grid min-h-[40vh] place-items-center"><LoaderCircle className="animate-spin text-primary" size={26} /></div> : (
        <>
          <Setup o={o} cvs={profile?.counts?.cvs ?? 0} onSeed={seed} seeding={seeding} />
          {o.profile.lastNotifyError && (
            <Notice tone="warning" title="WhatsApp alerts are waiting" action={<Link to={JH.candidate(o.profile.profileUid, 'alerts')}><Button size="sm" variant="secondary">Alert settings</Button></Link>}>
              {o.profile.lastNotifyError} <span className="text-text-muted">({timeAgo(o.profile.lastNotifyErrorAt)})</span>
            </Notice>
          )}
          {!o.profile.active && <Notice tone="warning" title="This candidate is paused">Their agents don't screen, score or alert until you turn the candidate back on.</Notice>}

          {o.agents.length > 0 && (
            <section className="mb-5">
              <div className="mb-2.5 flex items-center justify-between gap-2">
                <div className="jh-card-title"><Bot size={16} className="text-primary" />Agents <span className="jh-chip">{o.agents.filter((a) => a.active).length} active</span></div>
                <Link to={JH.AGENTS} className="inline-flex items-center gap-1 text-xs font-semibold text-primary">Manage <ArrowRight size={13} /></Link>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {o.agents.map((a) => (
                  <AgentCard key={a.agentUid} agent={a} profileUid={o.profile.profileUid} compact running={running && (runningUid === a.agentUid || !runningUid)}
                    onRun={() => { setRunningUid(a.agentUid); void start([a.agentUid], a.name); }} />
                ))}
              </div>
            </section>
          )}

          <div className="jh-kpis">
            <Kpi label="New this week" value={num(o.kpis.matchesThisWeek)} hint={`${o.queued} queued for AI`} icon={<Target size={15} />} tone="bg-primary-soft text-primary" />
            <Kpi label="Waiting for you" value={num(o.kpis.awaitingDecision)} hint={`${o.kpis.saved} saved`} icon={<Inbox size={15} />} tone="bg-warning-soft text-warning" />
            <Kpi label="Being prepared" value={num(o.kpis.inProgress)} hint="approved · needs input · ready" icon={<Sparkles size={15} />} tone="bg-info-soft text-info" />
            <Kpi label="Applied" value={num(o.kpis.applied)} hint={`${o.kpis.interviews} interview${o.kpis.interviews === 1 ? '' : 's'} · ${o.kpis.offers} offer${o.kpis.offers === 1 ? '' : 's'}`} icon={<Send size={15} />} tone="bg-success-soft text-success" />
            <Kpi label="Average fit" value={o.kpis.avgFit ?? '—'} hint="last 30 days" icon={<Award size={15} />} tone="bg-primary-soft text-primary" />
            <Kpi label="Jobs tracked" value={num(o.jobs.open)} hint={`+${o.jobs.last24h} in 24 h · ${o.employersWatched} employers watched`} icon={<Building2 size={15} />} tone="bg-surface-alt text-text-secondary" />
          </div>

          <div className="mt-5 grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
            <div className="space-y-5">
              <section className="jh-card jh-card-pad">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="jh-card-title"><Target size={16} className="text-primary" />Best open matches</div>
                    <div className="jh-card-sub">Highest fit first. Approve to get a cover note and screening answers.</div>
                  </div>
                  <Link to={JH.MATCHES} className="inline-flex items-center gap-1 text-xs font-semibold text-primary">All matches <ArrowRight size={13} /></Link>
                </div>
                <div className="mt-4 space-y-2.5">
                  {o.topMatches.length ? o.topMatches.map((a) => (
                    <MatchCard key={a.applicationUid} app={a} onOpen={() => setOpen(a.applicationUid)} onDecide={(act) => decide(a.applicationUid, act)} busy={busy?.uid === a.applicationUid ? busy.action : null} />
                  )) : (
                    <div className="rounded-xl border border-dashed border-border p-8 text-center">
                      <Bot className="mx-auto text-text-muted" size={26} />
                      <div className="mt-2 text-[13.5px] font-semibold">No open matches yet</div>
                      <p className="jh-muted mx-auto mt-1 max-w-sm">The agents keep polling. New jobs that clear an agent's bar with a sponsorship signal land here.</p>
                    </div>
                  )}
                </div>
              </section>

              <section className="jh-card jh-card-pad">
                <div className="jh-card-title"><TrendingUp size={16} className="text-primary" />Matches per day</div>
                <div className="jh-card-sub">Last 30 days — proposed vs. applied</div>
                <div className="mt-4 h-56">
                  {o.daily.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={o.daily} margin={{ top: 6, right: 6, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="jhP" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.35} /><stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} /></linearGradient>
                          <linearGradient id="jhA" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--color-success)" stopOpacity={0.35} /><stop offset="100%" stopColor="var(--color-success)" stopOpacity={0} /></linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-chart-grid)" vertical={false} />
                        <XAxis dataKey="day" tickFormatter={(d: string) => d.slice(5)} tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid var(--color-border)', background: 'var(--color-surface)', fontSize: 12 }} />
                        <Area type="monotone" dataKey="proposed" name="Proposed" stroke="var(--color-primary)" strokeWidth={2} fill="url(#jhP)" />
                        <Area type="monotone" dataKey="applied" name="Applied" stroke="var(--color-success)" strokeWidth={2} fill="url(#jhA)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <div className="grid h-full place-items-center jh-muted">The chart fills in as matches arrive.</div>}
                </div>
              </section>
            </div>

            <div className="space-y-5">
              <section className="jh-card jh-card-pad">
                <div className="jh-card-title"><Activity size={16} className="text-primary" />Funnel</div>
                <div className="jh-card-sub">Last 30 days for this candidate</div>
                <div className="mt-4 space-y-2.5">
                  {funnel.map(([label, n]) => (
                    <div key={label} className="jh-funnel-row">
                      <span className="text-text-secondary">{label}</span>
                      <div className="jh-funnel-bar"><span style={{ width: `${Math.max(n ? 3 : 0, (n / funnelMax) * 100)}%` }} /></div>
                      <span className="text-right font-semibold tabular-nums">{num(n)}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="jh-card jh-card-pad">
                <div className="jh-card-title"><MessageCircle size={16} className="text-primary" />Sponsorship signal</div>
                <div className="jh-card-sub">What the postings said, last 30 days</div>
                <div className="mt-3 flex h-2.5 overflow-hidden rounded-full bg-surface-alt">
                  {signals.map(({ s, n }) => n > 0 && <span key={s} style={{ width: `${(n / Math.max(1, signalTotal)) * 100}%`, background: `var(--color-${SIGNAL_META[s].tone === 'neutral' ? 'border-strong' : SIGNAL_META[s].tone})` }} />)}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {signals.map(({ s, n }) => <div key={s} className="flex items-center justify-between gap-2 rounded-lg bg-surface-alt/60 px-2.5 py-1.5"><SignalBadge signal={s} short /><span className="text-[13px] font-bold tabular-nums">{n}</span></div>)}
                </div>
              </section>

              <section className="jh-card jh-card-pad">
                <div className="jh-card-title">🌍 Where the matches are</div>
                <div className="mt-3 space-y-2">
                  {o.countries.length ? o.countries.map((c) => (
                    <div key={c.country} className="jh-funnel-row !grid-cols-[140px_1fr_40px]">
                      <span className="truncate">{flag(c.country)} {countryName(c.country)}</span>
                      <div className="jh-funnel-bar"><span style={{ width: `${(c.n / maxCountry) * 100}%` }} /></div>
                      <span className="text-right font-semibold tabular-nums">{c.n}</span>
                    </div>
                  )) : <p className="jh-muted">No matches yet.</p>}
                </div>
              </section>

              <section className="jh-card jh-card-pad">
                <div className="jh-card-title"><Coins size={16} className="text-primary" />AI budget</div>
                <div className="mt-3 space-y-3 text-[12.5px]">
                  <div><div className="mb-1 flex justify-between"><span className="text-text-secondary">This month</span><span className="font-semibold tabular-nums">{num(o.ai.monthCalls)} / {num(o.ai.monthLimit)} calls</span></div><Meter value={o.ai.monthCalls} max={o.ai.monthLimit} /></div>
                  <div><div className="mb-1 flex justify-between"><span className="text-text-secondary">Scored today · all agents</span><span className="font-semibold tabular-nums">{num(o.ai.dayScored)} / {num(o.ai.dayLimit)}</span></div><Meter value={o.ai.dayScored} max={o.ai.dayLimit} /></div>
                  <div className="flex justify-between text-text-muted"><span>Credits used this month</span><span className="tabular-nums">{o.ai.credits.toFixed(2)}</span></div>
                </div>
              </section>

              <section className="jh-card jh-card-pad">
                <div className="flex items-center justify-between">
                  <div className="jh-card-title"><Zap size={16} className="text-primary" />Agent runs</div>
                  <Link to={`${JH.SOURCES}#runs`} className="text-xs font-semibold text-primary">History</Link>
                </div>
                <div className="jh-card-sub">{lastRun ? `Last run ${timeAgo(lastRun.startedAt)} · ${o.sources.hot ?? 0} hot · ${o.sources.warm ?? 0} warm · ${o.sources.cold ?? 0} cold sources` : 'No runs yet.'}</div>
                <div className="mt-3 space-y-1.5">
                  {o.runs.slice(0, 5).map((r) => (
                    <div key={r.runUid} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-[12px] hover:bg-surface-alt">
                      <span className="inline-flex items-center gap-2">{r.finishedAt ? <span className="h-2 w-2 rounded-full bg-success" /> : <span className="jh-pulse" />}{fmtDateTime(r.startedAt)}</span>
                      <span className="text-text-muted">{r.jobsNew} new · {r.scored} scored · <b className="text-text-primary">{r.proposed} matched</b>{r.errorLog.length ? <TriangleAlert size={12} className="ml-1 inline text-warning" /> : null}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="jh-card jh-card-pad">
                <div className="jh-card-title"><BellRing size={16} className="text-primary" />Latest activity</div>
                <div className="mt-3 space-y-2">
                  {o.recent.map((a) => (
                    <button type="button" key={a.applicationUid} className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left text-[12.5px] hover:bg-surface-alt" onClick={() => setOpen(a.applicationUid)}>
                      <span className="min-w-0 truncate"><b>#{a.ref}</b> {a.job?.employerName} — {a.job?.title}</span>
                      <span className="shrink-0 text-text-muted">{STATUS_META[a.status].label}</span>
                    </button>
                  ))}
                  {!o.recent.length && <p className="jh-muted">Nothing yet.</p>}
                </div>
              </section>
              <div className="flex justify-end"><Button variant="ghost" size="sm" iconLeft={<UserRound size={14} />} onClick={() => nav(JH.candidate(o.profile.profileUid))}>Candidate settings</Button></div>
            </div>
          </div>
        </>
      )}
      <ApplicationDrawer applicationUid={open} onClose={() => setOpen(null)} onChanged={() => void load()} />
    </JobHunterShell>
  );
}
